/* eslint-disable no-unused-vars */
/* global React */
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Grid, Skeleton, SkeletonOwnProps } from '@mui/material'
import { useStyles } from '../hook'

/**
 * @typedef {Object} SkeletonProps
 * @property {Object} [style]                               optional skeleton styles
 * @property {string|number} [height='100%']                height of the skeleton
 * @property {string|number} [width='100%']                 width of the skeleton
 * @property {SkeletonOwnProps.variant} [variant='rounded'] variant of the skeleton
 * @property {number} [debounced=500]                       number in ms before the
 *                                                          skeleton appears
 */

const styles = theme => ({
  root: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    zIndex: 10,
  },
})

/**
 * Skeleton Higher Order Component
 *
 * Wrap `Component` with a skeleton that can be toggled using the `showSkeleton` callback.
 *
 * After `debounce` ms, it'll show the actual skeleton (if the setSkeleton) callback hasn't
 * been called by then. It's done so to avoid a flash of the skeleton before the data arrives.
 *
 * @param {React.JSXElement} Component  the component to wrap
 * @returns {function({skeletonProps: *, [p: string]: *}, *): null|JSX.Element}
 */
const SkeletonHOC = Component => ({ skeletonProps, ...props }, ref) => {
  const { isHidden = true } = props
  const { style, height = '100%', width = '100%', variant = 'rounded', debounce = 500 } = /** @type {SkeletonProps} */ skeletonProps || {}

  const classes = useStyles(styles)()

  const [hidden, setHidden] = useState(isHidden)
  const [skeleton, setSkeleton] = useState(true)

  const timer = useRef(setTimeout(() => setHidden(false), debounce))
  useEffect(() => () => clearTimeout(timer.current), [])

  const showSkeleton = useCallback((show) => { setSkeleton(show) }, [])

  const Child = useMemo(() => <Component
    ref={ref}
    {...props}
    events={{ ...props.events, showSkeleton }}
  />, [])

  return (
    <Grid
      item
      container
      height={'100%'}
      alignItems={'center'}
      justifyContent={'space-around'}
      style={{ position: 'relative' }}
    >
      {skeleton && (
        <Skeleton
          sx={{ ...style, opacity: hidden ? 0 : 1 }}
          className={classes.root}
          width={width}
          height={height}
          variant={variant}
        />
      )}
      {Child}
    </Grid>
  )
}

export default Component => forwardRef(SkeletonHOC(Component))
